import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Inbox, CheckCircle2, XCircle, ArrowLeft, ChevronDown, ChevronUp,
  Mail, Phone, User, Calendar, RefreshCw, ExternalLink, Globe, ArrowRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_TABS = [
  { key: "pending_approval", label: "Pending", color: "amber" },
  { key: "approved", label: "Approved", color: "emerald" },
  { key: "rejected", label: "Rejected", color: "rose" },
];

function StatusPill({ status }) {
  const map = {
    pending_approval: ["bg-amber-500/15 text-amber-300 border-amber-500/40", "Pending"],
    approved: ["bg-emerald-500/15 text-emerald-300 border-emerald-500/40", "Approved"],
    rejected: ["bg-rose-500/15 text-rose-300 border-rose-500/40", "Rejected"],
  };
  const [cls, label] = map[status] || ["bg-slate-700 text-slate-200", status];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`} data-testid={`status-pill-${status}`}>
      {label}
    </span>
  );
}

function TypePill({ type }) {
  const map = {
    academy: ["bg-blue-500/15 text-blue-300 border-blue-500/40", "Academy"],
    camp: ["bg-purple-500/15 text-purple-300 border-purple-500/40", "Camp"],
    trial: ["bg-pink-500/15 text-pink-300 border-pink-500/40", "Trial"],
  };
  const [cls, label] = map[type] || ["bg-slate-700/40 text-slate-300 border-slate-700", type || "—"];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`} data-testid={`type-pill-${type || "unknown"}`}>
      {label}
    </span>
  );
}

function RegistrationRow({ reg, onApprove, onReject, isWorking }) {
  const [open, setOpen] = useState(false);

  const created = reg.created_at ? new Date(reg.created_at).toLocaleString() : "—";
  const isPending = reg.status === "pending_approval";
  const childCount = (reg.children || []).length;
  const isTrial = reg.registration_type === "trial";

  return (
    <Card className="bg-slate-900/60 border-slate-800 p-4 mb-3" data-testid={`inbox-row-${reg.id}`}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-white truncate" data-testid="row-parent-name">
              {reg.parent_name || "—"}
            </h3>
            <StatusPill status={reg.status} />
            <TypePill type={reg.registration_type} />
            {reg.is_scc_member && (
              <Badge className="bg-blue-500/15 text-blue-300 border border-blue-500/40 text-xs">SCC member</Badge>
            )}
            {isTrial && reg.trial_meta?.preferred_slot && (
              <Badge className="bg-pink-500/10 text-pink-200 border border-pink-500/30 text-xs font-normal">
                Wants: {reg.trial_meta.preferred_slot}
              </Badge>
            )}
            {reg.status === "approved" && reg.confirmed_slot && (
              <Badge className="bg-emerald-500/10 text-emerald-200 border border-emerald-500/30 text-xs font-normal">
                Confirmed: {reg.confirmed_slot}
              </Badge>
            )}
            {isTrial && (
              <Badge className="bg-cyan-500/10 text-cyan-200 border border-cyan-500/30 text-xs font-normal"
                data-testid={`row-trial-stage-${reg.id}`}>
                Pipeline: {(reg.pipeline_stage || "new").replace("_", " ")}
              </Badge>
            )}
          </div>
          <div className="text-sm text-slate-400 mt-1 flex flex-wrap gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{reg.parent_email}</span>
            {reg.parent_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{reg.parent_phone}</span>}
            <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{childCount} child{childCount === 1 ? "" : "ren"}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{created}</span>
            <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" />{reg.source || "external"}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            onClick={() => setOpen((v) => !v)}
            data-testid={`row-toggle-${reg.id}`}
          >
            {open ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
            Details
          </Button>
          {isTrial ? (
            <Link to="/admin/trials" data-testid={`row-open-pipeline-${reg.id}`}>
              <Button size="sm" className="bg-cyan-600 hover:bg-cyan-500 text-white">
                <ArrowRight className="w-3 h-3 mr-1" />
                Open in pipeline
              </Button>
            </Link>
          ) : isPending && (
            <>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                onClick={() => onApprove(reg)}
                disabled={isWorking}
                data-testid={`row-approve-${reg.id}`}
              >
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Approve &amp; Sync
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-rose-500/50 text-rose-300 hover:bg-rose-500/10"
                onClick={() => onReject(reg)}
                disabled={isWorking}
                data-testid={`row-reject-${reg.id}`}
              >
                <XCircle className="w-3 h-3 mr-1" />
                Reject
              </Button>
            </>
          )}
          {reg.status === "approved" && reg.portal_registration_id && (
            <Link
              to={`/admin/payments?q=${encodeURIComponent(reg.parent_email || "")}`}
              data-testid={`row-open-portal-${reg.id}`}
            >
              <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                <ExternalLink className="w-3 h-3 mr-1" />
                Open in Payments Hub
              </Button>
            </Link>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-slate-800 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-testid={`row-details-${reg.id}`}>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{isTrial ? "Child" : "Children"}</div>
            {(reg.children || []).length === 0 && <div className="text-slate-500">No children attached.</div>}
            {(reg.children || []).map((c, i) => (
              <div key={i} className="bg-slate-800/50 rounded px-3 py-2 mb-1.5">
                <div className="text-white text-sm font-medium">{c.name || "Unnamed"}</div>
                <div className="text-xs text-slate-400">
                  {c.birthdate && <span className="mr-3">DOB: {c.birthdate}</span>}
                  {c.gender && <span className="mr-3">{c.gender}</span>}
                  {c.age_group_label && <span className="mr-3">{c.age_group_label}</span>}
                </div>
                {c.membership_number && (
                  <div className="text-xs text-slate-400 font-mono mt-1">{c.membership_number}</div>
                )}
              </div>
            ))}
            {isTrial && reg.trial_meta && (
              <div className="mt-3 bg-pink-500/5 border border-pink-500/20 rounded px-3 py-2">
                <div className="text-xs uppercase tracking-wider text-pink-300 mb-1">Trial preferences</div>
                <dl className="text-xs space-y-1">
                  {[
                    ["Preferred slot", reg.trial_meta.preferred_slot],
                    ["Backup slot", reg.trial_meta.secondary_slot],
                    ["Experience", reg.trial_meta.experience_level],
                    ["Parent notes", reg.trial_meta.notes],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <dt className="text-slate-500 min-w-[100px]">{k}:</dt>
                      <dd className="text-slate-200">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Registration data</div>
            <dl className="text-xs space-y-1.5">
              {[
                ["Registration ID", reg.id],
                ["Parent user ID", reg.parent_user_id || "— (trial lead)"],
                isTrial ? ["Confirmed slot", reg.confirmed_slot || "—"] : ["Camp", reg.camp_name || "—"],
                isTrial ? ["Ack email sent", String(reg.ack_email_sent ?? "—")] : ["Payment status", reg.payment_status || "—"],
                isTrial ? ["Confirm email sent", String(reg.confirm_email_sent ?? "—")] : ["Payment amount", reg.payment_amount != null ? `$${reg.payment_amount}` : "—"],
                isTrial ? ["Deposit status", reg.deposit_status || "none"] : ["Payment reference", reg.payment_reference || "—"],
                isTrial ? ["Converted to registration", reg.converted_to_registration_id || "—"] : ["Consents", reg.consents ? JSON.stringify(reg.consents) : "—"],
                ["Approved by", reg.approved_by_name || reg.approved_by || "—"],
                ["Approved at", reg.approved_at || "—"],
                ...(isTrial ? [] : [["Portal registration ID", reg.portal_registration_id || "—"]]),
                ...(isTrial ? [] : [["Activation email sent", String(reg.activate_email_sent ?? "—")]]),
                ["Rejected by", reg.rejected_by_name || reg.rejected_by || "—"],
                ["Rejected reason", reg.rejected_reason || "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <dt className="text-slate-500 min-w-[140px]">{k}:</dt>
                  <dd className="text-slate-200 break-all">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function AcademyInbox() {
  const [tab, setTab] = useState("pending_approval");
  const [typeFilter, setTypeFilter] = useState("all");  // all | academy | camp | trial
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({
    pending_approval: 0, approved: 0, rejected: 0, total: 0,
    by_type: { academy: 0, camp: 0, trial: 0 },
    pending_by_type: { academy: 0, camp: 0, trial: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Trial approve dialog state
  const [trialApproveFor, setTrialApproveFor] = useState(null);
  const [confirmedSlot, setConfirmedSlot] = useState("");
  const [trialExtraNotes, setTrialExtraNotes] = useState("");
  const [showHandledTrials, setShowHandledTrials] = useState(false);

  const fetchRows = async (statusKey = tab, typeKey = typeFilter) => {
    setLoading(true);
    try {
      const params = { status: statusKey };
      if (typeKey && typeKey !== "all") params.registration_type = typeKey;
      const r = await axios.get(`${API}/admin/external-registrations`, {
        params,
        withCredentials: true,
      });
      setRows(r.data.registrations || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Academy Inbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { status: tab };
        if (typeFilter && typeFilter !== "all") params.registration_type = typeFilter;
        const r = await axios.get(`${API}/admin/external-registrations`, {
          params,
          withCredentials: true,
        });
        if (cancelled) return;
        setRows(r.data.registrations || []);
        setCounts(r.data.counts || {});
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Failed to load Academy Inbox");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, typeFilter]);

  // Trials are run from the pipeline; once one is scheduled/declined there it's
  // "handled" and drops out of the inbox unless the admin asks to see them.
  const handledTrial = (r) => r.registration_type === "trial"
    && !["new", undefined, null, ""].includes(r.pipeline_stage);
  const visibleRows = showHandledTrials ? rows : rows.filter((r) => !handledTrial(r));
  const handledCount = rows.filter(handledTrial).length;
  const trialsWaiting = rows.filter(
    (r) => r.registration_type === "trial" && !handledTrial(r)).length;

  const handleApprove = async (reg) => {
    // Trials use a dedicated dialog because admin must enter the confirmed slot
    if (reg.registration_type === "trial") {
      setTrialApproveFor(reg);
      setConfirmedSlot(reg.trial_meta?.preferred_slot || "");
      setTrialExtraNotes("");
      return;
    }

    if (!window.confirm(
      `Approve "${reg.parent_name}" and sync to Portal?\n\n` +
      `This will:\n` +
      ` • Create a Portal registration (visible in Payments Hub)\n` +
      ` • Activate the parent + ${(reg.children || []).length} child account(s)\n` +
      ` • Email the parent a "set password" link (valid 7 days)\n\n` +
      `Fees and session enrollments will need to be completed manually.`
    )) return;
    setWorking(true);
    try {
      const r = await axios.put(
        `${API}/admin/external-registrations/${reg.id}/approve`,
        {},
        { withCredentials: true },
      );
      toast.success(r.data.message || "Approved");
      await fetchRows(tab, typeFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve");
    } finally {
      setWorking(false);
    }
  };

  const submitTrialApprove = async () => {
    if (!trialApproveFor) return;
    const slot = (confirmedSlot || "").trim();
    if (!slot) {
      toast.error("Confirmed slot is required");
      return;
    }
    setWorking(true);
    try {
      const r = await axios.put(
        `${API}/admin/external-registrations/${trialApproveFor.id}/approve-trial`,
        { confirmed_slot: slot, extra_notes: trialExtraNotes || null },
        { withCredentials: true },
      );
      toast.success(r.data.message || "Trial confirmed");
      setTrialApproveFor(null);
      setConfirmedSlot("");
      setTrialExtraNotes("");
      await fetchRows(tab, typeFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to confirm trial");
    } finally {
      setWorking(false);
    }
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    setWorking(true);
    try {
      const r = await axios.put(
        `${API}/admin/external-registrations/${rejectFor.id}/reject`,
        { reason: rejectReason },
        { withCredentials: true },
      );
      toast.success(r.data.message || "Rejected");
      setRejectFor(null);
      setRejectReason("");
      await fetchRows(tab, typeFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4" data-testid="academy-inbox-back">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <Inbox className="w-7 h-7 text-emerald-400" />
              Academy Inbox
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Registrations received from <span className="text-slate-200 font-mono">academy.sccfootball.com</span>. Approve to sync into the Portal and send the parent a &quot;set password&quot; email.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchRows(tab, typeFilter)}
            disabled={loading}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="academy-inbox-refresh"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        <div className="flex gap-2 mt-5 mb-3 flex-wrap" data-testid="academy-inbox-tabs">
          {STATUS_TABS.map((t) => {
            const active = t.key === tab;
            const count = counts[t.key] || 0;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                  active
                    ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-200"
                    : "bg-slate-900/50 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700"
                }`}
                data-testid={`academy-inbox-tab-${t.key}`}
              >
                {t.label} <span className="ml-1 opacity-70">({count})</span>
              </button>
            );
          })}
        </div>

        <div className="flex gap-2 mb-4 flex-wrap items-center" data-testid="academy-inbox-type-filter">
          <span className="text-xs uppercase tracking-wider text-slate-500 mr-1">Type:</span>
          {[
            { key: "all", label: "All", count: counts.total },
            { key: "academy", label: "Academy", count: counts.by_type?.academy || 0, pendingCount: counts.pending_by_type?.academy || 0 },
            { key: "camp", label: "Camp", count: counts.by_type?.camp || 0, pendingCount: counts.pending_by_type?.camp || 0 },
            { key: "trial", label: "Trial", count: counts.by_type?.trial || 0, pendingCount: counts.pending_by_type?.trial || 0 },
          ].map((t) => {
            const active = t.key === typeFilter;
            const shownCount = tab === "pending_approval" && t.pendingCount != null ? t.pendingCount : t.count;
            return (
              <button
                key={t.key}
                onClick={() => setTypeFilter(t.key)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                  active
                    ? "bg-pink-500/20 border-pink-500/60 text-pink-200"
                    : "bg-slate-900/50 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700"
                }`}
                data-testid={`academy-inbox-type-${t.key}`}
              >
                {t.label} <span className="ml-1 opacity-70">({shownCount})</span>
              </button>
            );
          })}
        </div>

        {(trialsWaiting > 0 || handledCount > 0) && (
          <Card className="bg-cyan-500/5 border-cyan-700/40 p-3 mb-3 flex flex-wrap items-center gap-3"
            data-testid="inbox-trials-banner">
            <p className="text-xs text-slate-300 flex-1 min-w-[220px]">
              Trials are run from the <Link to="/admin/trials" className="text-cyan-300 underline">Trials pipeline</Link> —
              scheduling there confirms the slot and emails the parent.
              {trialsWaiting > 0 && ` ${trialsWaiting} waiting to be scheduled.`}
              {handledCount > 0 && ` ${handledCount} already handled and tucked away.`}
            </p>
            {handledCount > 0 && (
              <Button size="sm" variant="outline" className="h-8 text-[11px] border-slate-600 text-slate-300"
                onClick={() => setShowHandledTrials((v) => !v)}
                data-testid="inbox-toggle-handled-trials">
                {showHandledTrials ? "Hide handled trials" : `Show ${handledCount} handled trial(s)`}
              </Button>
            )}
          </Card>
        )}

        {loading ? (
          <div className="text-slate-400 text-sm py-10 text-center">Loading…</div>
        ) : visibleRows.length === 0 ? (
          <Card className="bg-slate-900/60 border-slate-800 p-10 text-center text-slate-400" data-testid="academy-inbox-empty">
            No registrations in <span className="text-slate-200">{STATUS_TABS.find((t) => t.key === tab)?.label}</span>.
          </Card>
        ) : (
          visibleRows.map((reg) => (
            <RegistrationRow
              key={reg.id}
              reg={reg}
              onApprove={handleApprove}
              onReject={(r) => { setRejectFor(r); setRejectReason(""); }}
              isWorking={working}
            />
          ))
        )}
      </div>

      <Dialog open={!!rejectFor} onOpenChange={(o) => { if (!o) { setRejectFor(null); setRejectReason(""); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle>Reject registration</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-300">
            Rejecting <strong className="text-white">{rejectFor?.parent_name}</strong> ({rejectFor?.parent_email}). The parent will NOT be notified automatically.
          </div>
          <Textarea
            placeholder="Reason (optional, for audit log)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="bg-slate-800 border-slate-700 mt-2"
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button
              variant="outline"
              className="border-slate-700"
              onClick={() => { setRejectFor(null); setRejectReason(""); }}
              data-testid="reject-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-500"
              onClick={submitReject}
              disabled={working}
              data-testid="reject-confirm"
            >
              Confirm Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trial confirmation dialog — admin picks the confirmed slot which gets
          emailed to the parent. Pre-filled with the parent's preferred slot. */}
      <Dialog open={!!trialApproveFor} onOpenChange={(o) => { if (!o) { setTrialApproveFor(null); setConfirmedSlot(""); setTrialExtraNotes(""); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle>Confirm trial slot</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-300 space-y-2">
            <p>
              Confirming trial for{" "}
              <strong className="text-white">
                {(trialApproveFor?.children || [{}])[0]?.name || "—"}
              </strong>{" "}
              ({trialApproveFor?.parent_name} · {trialApproveFor?.parent_email}).
            </p>
            <div className="bg-slate-800/40 rounded px-3 py-2 text-xs space-y-1">
              {trialApproveFor?.trial_meta?.preferred_slot && (
                <div><span className="text-slate-500">Preferred:</span> <span className="text-slate-200">{trialApproveFor.trial_meta.preferred_slot}</span></div>
              )}
              {trialApproveFor?.trial_meta?.secondary_slot && (
                <div><span className="text-slate-500">Backup:</span> <span className="text-slate-200">{trialApproveFor.trial_meta.secondary_slot}</span></div>
              )}
              {trialApproveFor?.trial_meta?.experience_level && (
                <div><span className="text-slate-500">Experience:</span> <span className="text-slate-200">{trialApproveFor.trial_meta.experience_level}</span></div>
              )}
              {trialApproveFor?.trial_meta?.notes && (
                <div><span className="text-slate-500">Parent notes:</span> <span className="text-slate-200">{trialApproveFor.trial_meta.notes}</span></div>
              )}
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs text-slate-400 mb-1 block">Confirmed slot (will be emailed to parent)</label>
            <input
              type="text"
              value={confirmedSlot}
              onChange={(e) => setConfirmedSlot(e.target.value)}
              placeholder="e.g. Sat 09:00–10:00 U10 (Padang)"
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-pink-500"
              data-testid="trial-confirmed-slot-input"
            />
          </div>
          <div className="mt-3">
            <label className="text-xs text-slate-400 mb-1 block">Extra notes (optional)</label>
            <Textarea
              placeholder="e.g. Coach Chris will run this session. Bring goalie gloves if you have any."
              value={trialExtraNotes}
              onChange={(e) => setTrialExtraNotes(e.target.value)}
              className="bg-slate-800 border-slate-700"
              data-testid="trial-extra-notes-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="border-slate-700"
              onClick={() => { setTrialApproveFor(null); setConfirmedSlot(""); setTrialExtraNotes(""); }}
              data-testid="trial-approve-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-pink-600 hover:bg-pink-500"
              onClick={submitTrialApprove}
              disabled={working || !confirmedSlot.trim()}
              data-testid="trial-approve-confirm"
            >
              Send Confirmation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
